/**
 * Pure helper functions for bidirectional sync between two-point coordinates
 * and derived dimensions (length, width, gradient) in the double-point
 * creation dialog.
 *
 * These are stateless utilities extracted from
 * PropertyDialogSimpleStructureDoublePoint.vue to reduce file size and
 * improve testability.
 */

type Point3 = Readonly<{ x: number; y: number; z: number }>;

function canonicalizeSignedZero(value: number): number {
	const result = value;
	return result === 0 ? 0 : result;
}

/** Round a number to 3 decimal places, returning 0 for non-finite input. */
export function roundTo3(v: number | string): number {
	const n = Number(v);
	return Number.isFinite(n) ? canonicalizeSignedZero(Number(n.toFixed(3))) : 0;
}

/** Round all axes of a point to 3 decimal places. */
export function roundPointTo3(p: Partial<Point3>): {
	x: number;
	y: number;
	z: number;
} {
	return {
		x: p.x !== undefined ? roundTo3(p.x) : 0,
		y: p.y !== undefined ? roundTo3(p.y) : 0,
		z: p.z !== undefined ? roundTo3(p.z) : 0,
	};
}

/**
 * Compute pipe gradient (as slope-unit value, e.g. 1/n) and length from P1→P2.
 * Returns `{ gradientValue, length }`.
 * `gradientValue` is `100 / gradientPercent` when non-zero, else 0 — matching
 * the viewer convention used by SlopeSettingInput.
 */
export function computePipeGradientFromPoints(
	p1: Readonly<Point3>,
	p2: Readonly<Point3>,
): { gradientValue: number; length: number } {
	const dx = p2.x - p1.x;
	const dy = p2.y - p1.y;
	const dz = p2.z - p1.z;
	const hd = Math.sqrt(dx * dx + dy * dy);
	const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

	let gradientValue = 0;
	if (hd > 1e-10) {
		const gPct = (dz / hd) * 100;
		gradientValue = gPct !== 0 ? roundTo3(100 / gPct) : 0;
	}

	return { gradientValue, length };
}

/**
 * Compute new P2 position from a gradient percent change while keeping 3D length fixed.
 * Preserves the XY bearing direction of the original P1→P2 vector.
 */
export function computePipeP2FromGradient(
	p1: Readonly<Point3>,
	p2: Readonly<Point3>,
	gradientPercent: number,
	targetLength: number,
): { x: number; y: number; z: number } {
	const alpha = Math.atan(gradientPercent / 100);
	const newDz = targetLength * Math.sin(alpha);
	const newHorizDist = targetLength * Math.cos(alpha);

	const dx = p2.x - p1.x;
	const dy = p2.y - p1.y;
	const hd = Math.sqrt(dx * dx + dy * dy);
	const bearingX = hd > 1e-10 ? dx / hd : 1;
	const bearingY = hd > 1e-10 ? dy / hd : 0;

	return {
		x: p1.x + bearingX * newHorizDist,
		y: p1.y + bearingY * newHorizDist,
		z: p1.z + newDz,
	};
}

/** Maximum gradient percent allowed to prevent degenerate geometry. */
const MAX_GRADIENT_PERCENT = 9999.99;

/** Clamp gradient percent to the safe range used across SolidMesh double-point forms. */
export function clampGradientPercent(value: number): number {
	return canonicalizeSignedZero(
		Math.max(-MAX_GRADIENT_PERCENT, Math.min(MAX_GRADIENT_PERCENT, value)),
	);
}

/**
 * Compute the updated P1/P2 positions during a drag operation.
 *
 * Drag = translate: P1 follows the anchor XY and P2 moves by the same XY
 * delta (P1→P2 is rotation/length-invariant under translation). Z is
 * preserved to avoid jitter from frequent re-computation of 3D transforms.
 *
 * @param prevP1 - Previous P1 position
 * @param prevP2 - Previous P2 position
 * @param newP1 - New anchor position (only x/y used)
 * @returns Updated { p1, p2 } with Z preserved from previous values
 */
export function computeDragPoints(
	prevP1: Readonly<Point3>,
	prevP2: Readonly<Point3>,
	newP1: Readonly<Point3>,
): { p1: Point3; p2: Point3 } {
	const dx = newP1.x - prevP1.x;
	const dy = newP1.y - prevP1.y;
	return {
		p1: { ...prevP1, x: newP1.x, y: newP1.y },
		p2: {
			...prevP2,
			x: prevP2.x + dx,
			y: prevP2.y + dy,
		},
	};
}

/**
 * Convert a SlopeSettingInput viewer value to gradient percent.
 *
 * SlopeSettingInput emits different values depending on the unit suffix:
 * - Degree suffix ("°"): the value IS the gradient as a multiplier → `100 * value`
 * - Ratio suffix (default): the value is the denominator of 1:N → `100 / value`
 *
 * Clamps result to ±9999.99 to prevent degenerate
 * geometry from extreme inputs.
 *
 * @param viewerValue - The raw value from SlopeSettingInput
 * @param unitSuffix - The suffix string from the slope unit type (e.g. "°" or "%")
 * @returns gradient in percent, clamped to safe range
 */
export function slopeValueToGradientPercent(
	viewerValue: number,
	unitSuffix: string,
): number {
	if (!viewerValue || !Number.isFinite(viewerValue)) return 0;
	const raw = unitSuffix === "°" ? 100 * viewerValue : 100 / viewerValue;
	if (!Number.isFinite(raw)) return 0;
	return clampGradientPercent(raw);
}
